import React, { useState, useEffect } from 'react';
import { Shield, Ban, Trash2, Flag, Clock, User, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { moderationService, ModerationAction, BlockedUser } from '../../services/moderationService';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';

interface ModerationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  roomId?: string;
}

const ModerationPanel: React.FC<ModerationPanelProps> = ({ isOpen, onClose, roomId }) => {
  const { user } = useAuth();
  const { currentRoom } = useChat();
  const [activeTab, setActiveTab] = useState<'blocked' | 'history' | 'rules'>('blocked');
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [moderationHistory, setModerationHistory] = useState<ModerationAction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentRoomId = roomId || currentRoom?.id;

  useEffect(() => {
    if (isOpen && currentRoomId) {
      loadModerationData();
    }
  }, [isOpen, currentRoomId, activeTab]);

  const loadModerationData = async () => {
    if (!currentRoomId) return;

    setIsLoading(true);
    setError(null);

    try {
      if (activeTab === 'blocked') {
        const blocked = await moderationService.getBlockedUsers(currentRoomId);
        setBlockedUsers(blocked);
      } else if (activeTab === 'history') {
        const history = await moderationService.getModerationHistory(currentRoomId);
        setModerationHistory(history);
      }
    } catch (err) {
      setError('Erro ao carregar dados de moderação');
      console.error('Erro na moderação:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnblockUser = async (userId: string) => {
    if (!currentRoomId || !user) return;

    try {
      const success = await moderationService.unblockUser(currentRoomId, userId, user.id);
      if (success) {
        setBlockedUsers(prev => prev.filter(blocked => blocked.user_id !== userId));
      } else {
        setError('Falha ao desbloquear usuário');
      }
    } catch (err) {
      setError('Erro ao desbloquear usuário');
      console.error('Erro:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'delete_message':
        return <Trash2 className="w-4 h-4 text-nk-error" />;
      case 'block_user':
        return <Ban className="w-4 h-4 text-nk-error" />;
      case 'unblock_user':
        return <CheckCircle className="w-4 h-4 text-nk-success" />;
      case 'warn_user':
        return <AlertTriangle className="w-4 h-4 text-nk-warn" />;
      case 'flag_content':
        return <Flag className="w-4 h-4 text-orange-500" />;
      default:
        return <Shield className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionLabel = (actionType: string) => {
    switch (actionType) {
      case 'delete_message':
        return 'Mensagem Deletada';
      case 'block_user':
        return 'Usuário Bloqueado';
      case 'unblock_user':
        return 'Usuário Desbloqueado';
      case 'warn_user':
        return 'Usuário Advertido';
      case 'flag_content':
        return 'Conteúdo Reportado';
      default:
        return actionType;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-coral" />
            <h2 className="text-xl font-semibold">Painel de Moderação</h2>
            {currentRoom && (
              <span className="text-sm text-gray-500">• {currentRoom.name}</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('blocked')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'blocked'
                ? 'border-coral text-coral'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Ban className="w-4 h-4 inline mr-2" />
            Usuários Bloqueados
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-coral text-coral'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Histórico de Ações
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'rules'
                ? 'border-coral text-coral'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Regras
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-nk-error rounded-lg text-nk-error">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
              <span className="ml-3 text-gray-600">Carregando...</span>
            </div>
          ) : (
            <>
              {/* Usuários Bloqueados */}
              {activeTab === 'blocked' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">Usuários Bloqueados</h3>
                    <span className="text-sm text-gray-500">
                      {blockedUsers.length} usuário{blockedUsers.length !== 1 ? 's' : ''} bloqueado{blockedUsers.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {blockedUsers.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Ban className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p>Nenhum usuário bloqueado</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {blockedUsers.map((blocked) => (
                        <div key={blocked.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <span className="font-medium">
                                  {(blocked as any).user?.name || 'Usuário Desconhecido'}
                                </span>
                                <span className="text-sm text-gray-500">
                                  ({(blocked as any).user?.email})
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 mb-2">
                                <strong>Motivo:</strong> {blocked.reason}
                              </div>
                              <div className="text-sm text-gray-500">
                                <span>Bloqueado em {formatDate(blocked.created_at)}</span>
                                {blocked.expires_at && (
                                  <span className="ml-2">
                                    • Expira em {formatDate(blocked.expires_at)}
                                  </span>
                                )}
                                {!blocked.expires_at && (
                                  <span className="ml-2 text-nk-error">• Permanente</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                Por: {(blocked as any).blocked_by_user?.name || 'Desconhecido'}
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnblockUser(blocked.user_id)}
                              className="ml-4 px-3 py-1 text-sm bg-green-50 text-nk-success rounded-md hover:bg-green-100 transition-colors"
                            >
                              Desbloquear
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Histórico de Ações */}
              {activeTab === 'history' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">Histórico de Moderação</h3>
                    <span className="text-sm text-gray-500">
                      {moderationHistory.length} ação{moderationHistory.length !== 1 ? 'ões' : ''} registrada{moderationHistory.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {moderationHistory.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p>Nenhuma ação de moderação registrada</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {moderationHistory.map((action) => (
                        <div key={action.id} className="border rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            {getActionIcon(action.action_type)}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">
                                  {getActionLabel(action.action_type)}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {formatDate(action.created_at)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 mb-2">
                                <strong>Motivo:</strong> {action.reason}
                              </div>
                              <div className="text-xs text-gray-500">
                                <span>
                                  Usuário afetado: {(action as any).user?.name || 'Desconhecido'}
                                </span>
                                <span className="mx-2">•</span>
                                <span>
                                  Moderador: {(action as any).moderator?.name || 'Desconhecido'}
                                </span>
                              </div>
                              {action.metadata && Object.keys(action.metadata).length > 0 && (
                                <div className="mt-2 text-xs text-gray-400">
                                  <details>
                                    <summary className="cursor-pointer">Detalhes adicionais</summary>
                                    <pre className="mt-1 p-2 bg-gray-50 rounded text-xs">
                                      {JSON.stringify(action.metadata, null, 2)}
                                    </pre>
                                  </details>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Regras */}
              {activeTab === 'rules' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Regras de Moderação</h3>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-medium mb-3">Regras Automáticas Ativas:</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-nk-success" />
                        Filtro de palavras inadequadas
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-nk-success" />
                        Detecção de flood
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-nk-success" />
                        Bloqueio automático de usuários banidos
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-nk-success" />
                        Limite de tamanho de mensagem (2000 caracteres)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-nk-success" />
                        Proteção contra flood (máx. 10 msg/minuto)
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Como funciona:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Mensagens são verificadas automaticamente antes de serem enviadas</li>
                      <li>• Violações são registradas no histórico de moderação</li>
                      <li>• Admins podem bloquear/desbloquear usuários manualmente</li>
                      <li>• Bloqueios temporários expiram automaticamente</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModerationPanel;